import { Component, DestroyRef, ElementRef, HostListener, computed, effect, inject, signal, untracked, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator, MatPaginatorIntl } from '@angular/material/paginator';
import { HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { EntrySelectors } from 'src/app/state/entry.selectors';
import { EntryActions } from 'src/app/state/entry.actions';
import { UtilService } from 'src/app/util.service';
import { CommonService } from 'src/app/common.service';
import { ConfigService } from 'src/app/config.service';
import { MolstarPluginService } from 'src/app/molstar-plugin-service';
import { EntryData, LlmAnnotation, PlddtResidueData, Structure } from '../structure-data.models';
import { SmartSequenceAnnotation, SmartSequenceVisOptions } from '../../shared/smart-seq-viewer/viewer/data-processing/seq-viewer-models';
import { SmartSeqViewerComponent } from '../../shared/smart-seq-viewer/smart-seq-viewer.component';
import { PaeSelectedRegions } from 'src/app/pae-view/pae-view.component';
import { Chain, PaeDataService } from 'src/app/pae-view/pae-data.service';
import { DownloadOption } from '../entry-dropdown/entry-dropdown.component';
import { GoogleAnalyticsService } from 'src/app/google-analytics.service';
import {
  annotationsToCsv,
  getAnnotationCsvFileName,
  getAnnotationKey,
  getCircleAnnotationsForSeqViewer,
  getPublicationAuthorYear,
  getSentenceArticleLink,
  getUniformBackgroundAnnotation,
  isLicenseRestricted,
  parseLlmAnnotationsResponse,
  PublicationAuthorYear,
  sentenceNeedsTruncation,
  sliceChainConfidenceData,
  TEXT_ANNOTATION_HIGHLIGHT_COLOR,
  triggerCsvDownload,
} from './text-annotations.helpers';

interface TextAnnotationCard {
  tedNumber: number;
  label: string;
  displayTitle: string;
  displayChains: string;
  chainLabels: string[];
  uniprotAccession: string;
  uniprotDescription: string;
  // null when this card's own accession hasn't been fetched yet (llm_annotations is loaded
  // per active chain, not for every chain up front) — the real count is only known once the
  // user selects this card and its own data loads.
  annotationCount: number | null;
  // Every chain grouped under this card (e.g. both copies of a homodimer's macromolecule) —
  // lets the chain dropdown resolve the specific per-chain sequence/plddtDocUrl on selection.
  groupedSources: EntryData[];
  source: EntryData;
}

// "Results per page" (Figma) instead of Material's default "Items per page" — scoped to this
// component via the `providers` factory below, so it doesn't affect mat-paginators elsewhere.
function createTextAnnotationsPaginatorIntl(): MatPaginatorIntl {
  const intl = new MatPaginatorIntl();
  intl.itemsPerPageLabel = 'Results per page:';
  return intl;
}

@Component({
  selector: 'text-annotations-tab',
  templateUrl: './text-annotations-tab.component.html',
  styleUrl: './text-annotations-tab.component.css',
  standalone: false,
  providers: [{ provide: MatPaginatorIntl, useFactory: createTextAnnotationsPaginatorIntl }],
})
export class TextAnnotationsTabComponent {
  private store = inject(Store);
  private utilService = inject(UtilService);
  private el = inject(ElementRef);
  private molstarPluginService = inject(MolstarPluginService);
  private paeDataService = inject(PaeDataService);
  private commonService = inject(CommonService);
  private configService = inject(ConfigService);
  private gaService = inject(GoogleAnalyticsService);
  private readonly destroyRef = inject(DestroyRef);

  // Setter-based ViewChild: the <mat-paginator> only mounts once annotation data has
  // arrived (it's behind an @if), so a one-shot ngAfterViewInit would miss it if the
  // HTTP response resolves after the first view init.
  @ViewChild(MatPaginator) set paginator(value: MatPaginator | undefined) {
    this.dataSource.paginator = value;
  }

  // Setter-based for the same reason as `paginator` above — the viewer only mounts once the
  // sequence/pLDDT data has arrived (behind an @if), so a one-shot query would miss it.
  private seqViewerComponent?: SmartSeqViewerComponent;
  @ViewChild(SmartSeqViewerComponent) set seqViewer(value: SmartSeqViewerComponent | undefined) {
    this.seqViewerComponent = value;
  }

  // Sized for ~5 visible sequence rows: totalLineHeight (numbering box + residue box +
  // lineBottomMargin, with circle-annotation spacing) works out to ~40-42px/row from the
  // viewer's font-metric constants, so 5 rows lands around 210-220px. The canvas now scrolls
  // independently of the sidebar (see seq-viewer-ui-main-panels.ts), so this only affects the
  // canvas's own viewport height.
  public viewerOptions: SmartSequenceVisOptions = {
    grouping: true,
    groupingLineBreak: false,
    externalEvents: true,
    hoverTooltips: false,
    useAuthNumbers: true,
    helpLogoSrc: '/assets/img/help_outline_24px.svg',
    scrollContainerMaxHeight: 220,
  };

  public entryData = toSignal(this.store.select(EntrySelectors.entryData));
  public macromoleculesData = toSignal(this.store.select(EntrySelectors.macromoleculesData), { initialValue: [] as EntryData[] });
  public llmAnnotationsData = toSignal(this.store.select(EntrySelectors.llmAnnotationsData));
  private llmAnnotationsLoaded = toSignal(this.store.select(EntrySelectors.llmAnnotationsLoaded));

  // Gated to the currently *loaded* accession matching the active chain's own accession. The
  // llm_annotations API is per-UniProt-accession, and for a heterodimer llmAnnotationsData only
  // ever holds ONE chain's data at a time (see the constructor effect below that re-fetches on
  // chain switch) — while that re-fetch is in flight this returns [] rather than briefly showing
  // the previous chain's now-mismatched annotations plotted against the new chain's sequence.
  // Monomer/homodimer are unaffected: their active accession never changes after the initial
  // entry-level load, so `loaded.uniprotAccession` always matches once that load lands.
  public annotations = computed<LlmAnnotation[]>(() => {
    const loaded = this.llmAnnotationsData();
    if (!loaded) return [];
    const activeAccession = this.activeUniprotAccession();
    if (this.isComplex() && activeAccession && loaded.uniprotAccession && loaded.uniprotAccession !== activeAccession) {
      return [];
    }
    return loaded.data ?? [];
  });
  public isComplex = computed(() => this.entryData()?.isComplex === true);

  // Mirrors MacromoleculesTabComponent's mappedMacromolecules: every chain gets a card (not
  // just ones the fetched annotation data happens to cover), and a homodimer's multiple
  // identical chains collapse into ONE card with a chain dropdown, same
  // grouping-by-composite-key pattern.
  // annotationCount is a genuine per-card count when we actually have that card's own data —
  // llm_annotations is fetched per UniProt accession, so a card only has real data once its
  // own accession has been loaded (either it's the active chain, or it shares an accession
  // with the active chain, e.g. NS3/NS5). Only then can we filter the loaded payload down to
  // that card's own residue range. A different-accession sibling that hasn't been visited yet
  // gets `null` — its true count isn't known until the user selects it and it's fetched.
  // Monomer entries never get a card at all (per designer input, decision #5): a single chain
  // has nothing to switch between, so the sidebar/dropdown/card machinery is homo/heterodimer-
  // only — monomer reads entryData() directly everywhere below instead.
  public chainCards = computed<TextAnnotationCard[]>(() => {
    if (!this.isComplex()) return [];

    const entryData = this.entryData();
    if (!entryData) return [];

    const loaded = this.llmAnnotationsData();

    const rows = this.macromoleculesData().filter((row) => !!(row as any).chainId);
    // uniprotAccession alone isn't a reliable group key: a polyprotein cleaved into separate
    // chains (e.g. NS3/NS5) can report the SAME accession for genuinely different chains with
    // different residue ranges (AFPDBE-1435) — see UtilService.groupMacromoleculeChains.
    const groups = this.utilService.groupMacromoleculeChains(rows);

    const mapped = groups.map((group, index) => {
      const representative = group[0];
      const chainLabels = Array.from(new Set(group.map((row) => (row as any).chainId as string))).sort((a, b) => a.localeCompare(b));
      const matchesLoaded = !!loaded?.uniprotAccession && loaded.uniprotAccession === representative.uniprotAccession;
      const range = matchesLoaded ? this.utilService.getMacromoleculeResidueRange(representative) : null;
      const annotationCount = matchesLoaded && range
        ? (loaded?.data ?? []).filter((a) => a.uniprotResidue >= range.start && a.uniprotResidue <= range.end).length
        : null;
      return {
        tedNumber: index + 1,
        label: representative.uniprotAccession || '-',
        displayTitle: representative.uniprotDescription || representative.uniprotAccession || `Macromolecule ${index + 1}`,
        displayChains: chainLabels.length > 0 ? `Chain ${chainLabels.join(', ')}` : 'Chain data unavailable',
        chainLabels,
        uniprotAccession: representative.uniprotAccession,
        uniprotDescription: representative.uniprotDescription,
        annotationCount,
        groupedSources: group,
        source: representative,
      };
    });

    mapped.sort((a, b) => (a.chainLabels[0] || '').localeCompare(b.chainLabels[0] || ''));
    return mapped.map((card, index) => ({ ...card, tedNumber: index + 1 }));
  });

  public activeCard = signal<TextAnnotationCard | null>(null);
  public isSidebarDisplayed = signal<boolean>(true);
  // Monomer entries never show the chain sidebar (or its toggle button) — a single chain has
  // nothing to switch between, so the tab is always full-width. Homo/heterodimer entries keep
  // the existing toggleable behaviour.
  public isSidebarVisible = computed(() => this.isComplex() && this.isSidebarDisplayed());
  public residueFilter = signal<number | undefined>(undefined);
  public isCopySequence = signal<boolean>(false);

  // Which specific chain within the active card is showing — only meaningfully different from
  // activeCard()'s first chain for a homodimer card (multiple chains grouped under one card),
  // where the chain dropdown lets the user pick a different physical copy. Defaulted/reset by
  // the constructor effect below whenever activeCard() changes.
  private selectedChainId = signal<string | undefined>(undefined);
  public chainDropdownOptions = signal<DownloadOption[]>([]);
  public chainDropdownSelected = signal<string>('');
  public showChainDropdown = computed(() => (this.activeCard()?.chainLabels?.length ?? 0) > 1);

  // Monomer has no card (see chainCards above), but Mol* chain-scoping (selectAnnotationResidue,
  // the sequence-viewer's [chainId] input feeding its protvista-click dispatch) still needs a
  // real chain id once entryData has loaded — falls back to entryData's own chainId (same source
  // the removed synthetic monomer card used to read), 'A' if that's missing too.
  public activeChainId = computed(() => {
    const card = this.activeCard();
    if (card) return this.selectedChainId() ?? card.chainLabels[0];
    const entryData = this.entryData();
    return entryData ? (entryData as any).chainId || 'A' : undefined;
  });

  // Resolves the specific per-chain EntryData row for whichever chain is currently selected
  // (matters for a homodimer card, whose groupedSources holds one row per physical chain).
  public activeChainSource = computed<EntryData | undefined>(() => {
    const card = this.activeCard();
    if (!card) return this.entryData();
    const chainId = this.activeChainId();
    return card.groupedSources.find((row) => (row as any).chainId === chainId) ?? card.source;
  });

  // Fragment of a larger UniProt protein (start !== 1) — surfaced next to the UniProt
  // accession in the template (AFPDBE-1445 follow-up).
  public activeUniprotRange = computed(() => this.utilService.getMacromoleculeResidueRange(this.activeChainSource()));

  public activeSequence = computed(() => this.activeChainSource()?.sequence || this.entryData()?.sequence || '');
  public activeEntityId = computed(() => this.activeChainSource()?.modelEntityId || this.entryData()?.modelEntityId);

  // The UniProt accession the on-screen chain actually belongs to — for a heterodimer's Chain B
  // this is Chain B's own accession, not the entry's representative Chain A accession. Drives
  // both the `annotations` staleness gate above and the re-fetch effect below.
  public activeUniprotAccession = computed(() => this.activeChainSource()?.uniprotAccession || this.entryData()?.uniprotAccession);

  // Each macromolecule/chain in a complex has its own prediction, so its own pLDDT doc —
  // re-fetched whenever the active chain (and therefore its plddtDocUrl) changes.
  private activePlddtDocUrl = computed(() => this.activeChainSource()?.plddtDocUrl || this.entryData()?.plddtDocUrl);
  public plddtData = signal<PlddtResidueData | undefined>(undefined);
  // Gates the sequence viewer's first render so it never shows the neutral gray fallback and
  // then flips to real pLDDT colours a moment later — the viewer only mounts once this fetch
  // has actually settled (success, failure, or no URL at all), so backgroundDataInput() is
  // already correct on the very first render.
  public plddtLoadAttempted = signal<boolean>(false);

  // No explicit chain filter here — `annotations()` itself is already scoped to the active
  // chain's own accession (llmAnnotationsData holds one accession's worth at a time, re-fetched
  // on chain switch; see the constructor effect and `annotations`'s own staleness gate above).
  public activeChainAnnotations = computed<LlmAnnotation[]>(() => this.annotations());

  // llm_annotations is fetched per UniProt ACCESSION, not per AFDB entry/chain — for a
  // polyprotein cleaved into fragments (e.g. NS3/NS5 sharing one accession), the response
  // covers the whole protein, including residues that belong to other fragments entirely.
  // Narrow to only the annotations whose uniprotResidue actually falls inside this chain's
  // own residue window before using them anywhere (table, sequence-viewer dots, Mol*).
  public inRangeAnnotations = computed<LlmAnnotation[]>(() => {
    const range = this.utilService.getMacromoleculeResidueRange(this.activeChainSource());
    const chainAnnotations = this.activeChainAnnotations();
    if (!range) return chainAnnotations;
    return chainAnnotations.filter((a) => a.uniprotResidue >= range.start && a.uniprotResidue <= range.end);
  });

  public getActiveChainTitle(): string {
    const card = this.activeCard();
    if (!card) {
      // Monomer: same fallback activeChainId() uses, since there's no card to read a
      // pre-formatted displayChains string from.
      const chainId = this.activeChainId();
      return chainId ? `Chain ${chainId}` : 'Chain';
    }
    if (this.showChainDropdown()) {
      return this.chainDropdownSelected() || this.chainDropdownOptions()[0]?.name || 'Chain';
    }
    return card.displayChains || 'Chain data unavailable';
  }

  public fastaTitle = computed(() => {
    const card = this.activeCard();
    const entryData = this.entryData();
    const accession = entryData?.modelEntityId || entryData?.uniprotAccession || '';
    const displayTitle = card?.displayTitle ?? entryData?.uniprotDescription ?? entryData?.uniprotAccession ?? '';
    return `>FASTA afdb|${accession}|${displayTitle}; ${this.getActiveChainTitle()}`;
  });

  public filteredAnnotations = computed<LlmAnnotation[]>(() => {
    const residue = this.residueFilter();
    const chainAnnotations = this.inRangeAnnotations();
    if (residue === undefined) return chainAnnotations;
    return chainAnnotations.filter((a) => a.uniprotResidue === residue);
  });

  public tableSearchTerm = signal<string>('');

  // Rows the user has expanded past the 5-line "Show more" truncation, keyed by
  // getAnnotationKey (LlmAnnotation has no stable id field of its own). Cleared implicitly
  // whenever the underlying annotation set changes, since a stale key just never matches again.
  private expandedSentenceKeys = signal<ReadonlySet<string>>(new Set());

  public searchedAnnotations = computed<LlmAnnotation[]>(() => {
    const term = this.tableSearchTerm().trim().toLowerCase();
    const annotations = this.filteredAnnotations();
    if (!term) return annotations;
    return annotations.filter((a) =>
      a.sentence.toLowerCase().includes(term) ||
      String(a.pubmedId ?? '').includes(term) ||
      String(a.publicationYear ?? '').includes(term) ||
      // All authors, not just the first one the table displays — pending team decision (2026-09-28).
      (a.authors ?? []).some((author) => author.toLowerCase().includes(term))
    );
  });

  public circleAboveDataInput = computed<SmartSequenceAnnotation>(() => {
    const rangeStart = this.utilService.getMacromoleculeResidueRange(this.activeChainSource())?.start ?? 1;
    return getCircleAnnotationsForSeqViewer(this.inRangeAnnotations(), rangeStart);
  });

  // One dot per distinct uniprotResidue — same dedup circleAboveDataInput already does, just
  // exposed as a count for the "Annotated residues" summary row.
  public annotatedResiduesCount = computed(() => this.circleAboveDataInput().data.length);

  // True while the on-screen chain's annotations haven't arrived yet (first load, or a
  // heterodimer chain switch re-fetching by its own accession) — the count shows a spinner, not 0.
  public activeChainAnnotationsLoading = computed(() => {
    if (!this.isComplex()) return !this.llmAnnotationsLoaded();
    const accession = this.activeUniprotAccession();
    return !!accession && this.llmAnnotationsData()?.uniprotAccession !== accession;
  });

  // Local residue numbers that get a green sphere on Mol* — same residues as the sequence viewer's dots.
  private molstarDotResidues = computed<number[]>(() => {
    const rangeStart = this.utilService.getMacromoleculeResidueRange(this.activeChainSource())?.start ?? 1;
    const local = this.inRangeAnnotations().map((a) => a.uniprotResidue - rangeStart + 1);
    return Array.from(new Set(local)).sort((a, b) => a - b);
  });

  // Always shown now, including for a heterodimer: llm_annotations is re-fetched per-chain on
  // selection (see the constructor effect below), and `annotations`'s staleness gate keeps this
  // at 0 rather than a stale/mismatched count while that re-fetch is in flight — so unlike the
  // old "one representative accession for the whole entry" behaviour, whatever's on screen is
  // always the active chain's own count.
  public showAnnotatedResiduesRow = computed(() => true);

  // A complex entry's confidence file is one shared payload for the whole assembly (residue
  // numbers run continuously across every chain, not restarting at 1 per chain) — has to be
  // sliced down to the active chain's own local numbering before it can be matched against
  // activeSequence(). See sliceChainConfidenceData's own doc comment for the full explanation.
  private activeChainPlddtData = computed<PlddtResidueData | undefined>(() => {
    const plddtData = this.plddtData();
    const chainId = this.activeChainId();
    if (!plddtData || !chainId) return plddtData;
    return sliceChainConfidenceData(plddtData, chainId);
  });
  public backgroundDataInput = computed<SmartSequenceAnnotation>(() => getUniformBackgroundAnnotation(this.activeSequence(), this.activeChainPlddtData()));

  // Per Figma: annotations at or above this confidence read as the norm (no badge); anything
  // below gets a "Low score" indicator on the row instead of a numeric score column.
  public readonly HIGH_CONFIDENCE_THRESHOLD = 0.99;

  // Shown in the Annotations column instead of the mined sentence when the source publication's
  // licence doesn't permit sharing the text (permittingLicense !== 'Y').
  public readonly COPYRIGHT_RESTRICTED_MESSAGE =
    'The text-mined annotation for this residue cannot be shared due to copyright restrictions for the related publication.';

  public displayedColumns = ['annotations', 'publication'];
  public dataSource = new MatTableDataSource<LlmAnnotation>([]);

  // Mol* + PAE — mirrors AnnotationsTabComponent's own working implementation, with
  // AlphaMissense-specific pieces dropped (this tab has no AM data) and different DOM
  // ids/classes so the two tabs' instances never collide during mat-tab-group's
  // destroy-then-recreate transition (see text-annotations-molstar-container in the template).
  public pdbeMolstar: any;
  public structure: Structure;
  public lastUpdateDate: string;
  public molLoadComplete: boolean = false;
  private molstarInitAttempts = 0;
  private static readonly MAX_MOLSTAR_INIT_ATTEMPTS = 20;
  private molstarInitialTimeoutId: any;
  private molstarRetryTimeoutId: any;
  private isDestroyed = false;
  private molstarInitTriggered = false;

  constructor() {
    effect(() => {
      const cards = this.chainCards();
      const currentSelection = this.activeCard();
      if (cards.length === 0) {
        this.activeCard.set(null);
        return;
      }
      const stillPresent = cards.find((card) => card.uniprotAccession === currentSelection?.uniprotAccession && card.chainLabels[0] === currentSelection?.chainLabels[0]);
      // chainCards is rebuilt (new objects) whenever annotations load; keep the current object
      // for the same card so the chain-dropdown effect doesn't reset the user's chain choice.
      if (stillPresent && currentSelection) return;
      this.activeCard.set(stillPresent ?? cards[0]);
    });

    // Heterodimer chain switch: llm_annotations is per-UniProt-accession, so a chain whose own
    // accession differs from whatever's currently loaded needs its own fetch — re-dispatch
    // whenever activeUniprotAccession stops matching llmAnnotationsData's accession. No-ops for
    // monomer (isComplex() false) and for a homodimer (both physical chains share one accession,
    // so it never mismatches once the initial entry-level load — dispatched once from
    // entry.component.ts on entry load — lands).
    effect(() => {
      if (!this.isComplex()) return;
      const accession = this.activeUniprotAccession();
      if (!accession) return;
      const loaded = this.llmAnnotationsData();
      if (loaded?.uniprotAccession === accession) return;
      this.store.dispatch(EntryActions.getLlmAnnotationsData({ uniprotAccession: accession }));
    });

    // Whenever the active card changes (sidebar click, or the card list itself rebuilding),
    // rebuild the chain dropdown for it and default to its first chain — mirrors
    // MacromoleculesTabComponent's syncChainDropdownForActiveMacromolecule +
    // handleSelectedCard's Mol* chain-focus, kept together since they always happen in lockstep
    // here (unlike Macromolecules tab, which separates them across two call sites).
    effect(() => {
      const card = this.activeCard();
      if (!card) {
        this.chainDropdownOptions.set([]);
        this.chainDropdownSelected.set('');
        this.selectedChainId.set(undefined);
        return;
      }
      const options: DownloadOption[] = card.chainLabels.map((label) => ({
        name: `Chain ${label}`,
        url: `chain-${label}`,
        downloadable: false,
      }));
      this.chainDropdownOptions.set(options);
      this.chainDropdownSelected.set(options[0]?.name || '');
      this.selectedChainId.set(card.chainLabels[0]);
    });

    // Redraw Mol*'s default view (green annotation dots + other-chain dimming) whenever the
    // active chain or its annotations change. Before Mol* has loaded this no-ops; the
    // loadComplete handler draws it once ready.
    effect(() => {
      this.activeChainId();
      this.molstarDotResidues();
      untracked(() => this.refreshMolstarBaseView());
    });

    effect(() => {
      this.dataSource.data = this.searchedAnnotations();
      this.dataSource.paginator?.firstPage();
    });

    effect(() => {
      const url = this.activePlddtDocUrl();
      this.plddtData.set(undefined);
      this.plddtLoadAttempted.set(false);
      if (!url) {
        this.plddtLoadAttempted.set(true);
        return;
      }
      this.fetchPlddtData(url);
    });

    // One-shot: entryData is undefined until the store first emits (toSignal has no
    // initialValue here), so guard against calling initMolstar() — which reads
    // this.entryData().bcifUrl unguarded — before it's ready.
    effect(() => {
      const entryData = this.entryData();
      if (!entryData || this.molstarInitTriggered) return;
      this.molstarInitTriggered = true;
      this.initMolstar();
    });

    this.destroyRef.onDestroy(() => {
      this.isDestroyed = true;
      if (this.molstarInitialTimeoutId) clearTimeout(this.molstarInitialTimeoutId);
      if (this.molstarRetryTimeoutId) clearTimeout(this.molstarRetryTimeoutId);
      this.molstarContainer?.removeEventListener('PDB.molstar.click', this.onMolstarClick);
      this.emptyClickSubscription?.unsubscribe();
      this.pdbeMolstar?.plugin?.dispose?.();
    });
  }

  private async fetchPlddtData(url: string): Promise<void> {
    try {
      const response = await fetch(url);
      if (!response.ok) return;
      const data = await response.json();
      // Guard against a slower, now-stale response landing after the active chain changed again.
      if (this.activePlddtDocUrl() === url) {
        this.plddtData.set(data);
      }
    } catch {
      // Leave plddtData unset — backgroundDataInput falls back to the flat neutral colour.
    } finally {
      if (this.activePlddtDocUrl() === url) {
        this.plddtLoadAttempted.set(true);
      }
    }
  }

  public onSearchInput(value: string): void {
    this.tableSearchTerm.set(value);
  }

  public isLowScore(annotation: LlmAnnotation): boolean {
    return (annotation.aiScore ?? 0) < this.HIGH_CONFIDENCE_THRESHOLD;
  }

  public isLicenseRestricted(annotation: LlmAnnotation): boolean {
    return isLicenseRestricted(annotation.permittingLicense);
  }

  public sentenceNeedsTruncation(annotation: LlmAnnotation): boolean {
    return sentenceNeedsTruncation(annotation.sentence);
  }

  public isSentenceExpanded(annotation: LlmAnnotation): boolean {
    return this.expandedSentenceKeys().has(getAnnotationKey(annotation));
  }

  public toggleSentenceExpanded(annotation: LlmAnnotation): void {
    const key = getAnnotationKey(annotation);
    this.expandedSentenceKeys.update((keys) => {
      const next = new Set(keys);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }

  public toggleSidebar(): void {
    this.isSidebarDisplayed.update((prev) => !prev);
  }

  public onCardClick(card: TextAnnotationCard): void {
    this.activeCard.set(card);
    this.residueFilter.set(undefined);
  }

  // Homodimer chain dropdown — switches which physical chain (of the same macromolecule) is
  // shown: sequence, pLDDT coloring, and Mol* focus all follow the selection.
  public onChainDropdownSelect(optionName: string): void {
    this.chainDropdownSelected.set(optionName);
    const chainId = optionName.replace(/^Chain\s+/i, '').trim();
    this.selectedChainId.set(chainId);
    this.residueFilter.set(undefined);
  }

  // Serialises Mol* calls so a redraw and a residue selection can't interleave their awaits.
  private molstarQueue: Promise<void> = Promise.resolve();
  private queueMolstar(task: () => Promise<void>): Promise<void> {
    this.molstarQueue = this.molstarQueue.then(task).catch((e) => console.error('[TextAnnotations] Mol* update failed', e));
    return this.molstarQueue;
  }

  // Default Mol* view: green spheres on the active chain's annotated residues, plus (complex
  // only) dimming/focus for the active chain. The dots select deliberately omits
  // keepRepresentations so it replaces any previous dots/ball-and-stick; every later select
  // must pass keepRepresentations: true or the dots are wiped.
  public refreshMolstarBaseView(): Promise<void> {
    return this.queueMolstar(() => this.drawMolstarBaseView());
  }

  private async drawMolstarBaseView(): Promise<void> {
    if (!this.molLoadComplete || !this.pdbeMolstar) return;
    const chainId = this.activeChainId();
    const chainScope = chainId ? { auth_asym_id: chainId } : {};
    const residues = this.molstarDotResidues();
    if (residues.length > 0) {
      await this.pdbeMolstar.visual.select({
        data: residues.map((residue) => ({
          residue_number: residue,
          ...chainScope,
          label_atom_id: 'CA',
          representation: 'spacefill',
          representationColor: TEXT_ANNOTATION_HIGHLIGHT_COLOR,
          representationOpacity: 0.8,
          color: null
        })),
        keepColors: true,
        keepOpacity: true,
      });
    } else {
      await this.pdbeMolstar.visual.clearSelection();
    }
    if (this.isComplex() && chainId) {
      await this.applySelectedChainToMolstar(chainId);
    }
  }

  // Focuses Mol* on the selected chain and dims every other known chain — mirrors
  // MacromoleculesTabComponent's applySelectedChainToMolstar/applyCardChainSelectionToMolstar.
  private async applySelectedChainToMolstar(chainId: string): Promise<void> {
    if (!this.molLoadComplete || !this.pdbeMolstar || !chainId) return;
    const allChainIds = this.chainCards().flatMap((card) => card.chainLabels);
    const otherChainIds = allChainIds.filter((id) => id !== chainId);
    await this.pdbeMolstar.visual.select({
      data: otherChainIds.map((id) => ({ auth_asym_id: id, color: null, opacity: 0.4 })),
      keepRepresentations: true
    });
    await this.pdbeMolstar.visual.focus([{ auth_asym_id: chainId }]);
  }

  // residueFilter/filteredAnnotations compare against absolute uniprotResidue values, while
  // Mol*'s selectAnnotationResidue expects the chain's own local/author numbering — these
  // convert between the two using the active chain's own residue range (a no-op, start=1,
  // for an ordinary chain that isn't a fragment of a larger UniProt accession).
  private toLocalResidue(uniprotResidue: number): number {
    const start = this.utilService.getMacromoleculeResidueRange(this.activeChainSource())?.start ?? 1;
    return uniprotResidue - start + 1;
  }

  private toAbsoluteResidue(localResidue: number): number {
    const start = this.utilService.getMacromoleculeResidueRange(this.activeChainSource())?.start ?? 1;
    return localResidue + start - 1;
  }

  // annotation.uniprotResidue is absolute — store it as-is for the table filter, but Mol*
  // and the sequence viewer both need the chain-local translation.
  public filterByResidue(annotation: LlmAnnotation): void {
    this.residueFilter.set(annotation.uniprotResidue);
    const localResidue = this.toLocalResidue(annotation.uniprotResidue);
    this.selectAnnotationResidue(localResidue);
    this.selectSeqViewerResidue(localResidue);
  }

  // Selecting a table row only drove Mol* — the sequence viewer's own canvas highlight and
  // right-side residue-details sidebar never updated, since selectAnnotationResidue only talks
  // to Mol*. Drives the viewer directly via its own public selectResidue (see
  // SmartSeqViewerComponent) rather than the library's DOM-event external-click path
  // ('to-seq-viewer-click') — that event has several silent-failure points (container-not-yet-
  // mounted at construction time, entityId/chainId guard, listener re-registered on every
  // instance recreation) that aren't easy to verify without a live browser, so a direct
  // ViewChild call is both simpler and more reliably debuggable. Passes local numbering
  // (matching handleSeqViewerResidueClick's own detail.start convention).
  private selectSeqViewerResidue(localResidue: number): void {
    this.seqViewerComponent?.selectResidue(localResidue);
  }

  public clearResidueFilter(): void {
    this.residueFilter.set(undefined);
    this.seqViewerComponent?.clearResidueSelection();
    if (this.molLoadComplete) {
      this.pdbeMolstar.visual.reset({ camera: true });
      // Removes the residue's ball-and-stick, keeps the dots and restores chain focus.
      this.refreshMolstarBaseView();
    }
  }

  public copySequence(): void {
    const sequence = this.activeSequence();
    if (!sequence) return;
    this.utilService.copy(`${this.fastaTitle()}\n${sequence}`);
    this.isCopySequence.set(true);
    setTimeout(() => this.isCopySequence.set(false), 2000);
  }

  // ---- CSV download ----------------------------------------------------------------------

  // Table link: exactly the rows the table currently holds across all paginator pages — active
  // chain's residue range, plus any residue-click filter and search term.
  public downloadTableCsv(): void {
    const rows = this.searchedAnnotations();
    if (rows.length === 0) return;
    this.gaService.emitEventWithParams('text_ai_annotations_table_download', {
      entry_type: this.getEntryTypeForGa(),
      assembly_type: this.getAssemblyTypeForGa(),
    });
    triggerCsvDownload(annotationsToCsv(rows, this.COPYRIGHT_RESTRICTED_MESSAGE), getAnnotationCsvFileName());
  }

  private getEntryTypeForGa(): 'monomer' | 'multimer' {
    return this.entryData()?.isComplex ? 'multimer' : 'monomer';
  }

  private getAssemblyTypeForGa(): 'homo' | 'hetero' | 'mono' {
    if (!this.entryData()?.isComplex) {
      return 'mono';
    }

    const assemblyType = (this.entryData()?.assemblyType || '').toLowerCase();
    if (assemblyType === 'homo' || assemblyType === 'hetero') {
      return assemblyType;
    }

    return 'mono';
  }

  // Sidebar link (complex only): the whole llm_annotations response for EVERY distinct accession
  // in the complex, unfiltered. The store only ever holds the active chain's accession, so any
  // other accession is fetched here on demand (and cached for repeat clicks).
  public isDownloadingAll = signal<boolean>(false);
  public downloadAllFailed = signal<boolean>(false);
  private downloadedAccessionData = new Map<string, LlmAnnotation[]>();

  public async downloadAllCsv(): Promise<void> {
    if (this.isDownloadingAll()) return;
    const accessions = Array.from(new Set(this.chainCards().map((card) => card.uniprotAccession).filter(Boolean)));
    if (accessions.length === 0) return;
    this.gaService.emitEventWithParams('text_ai_annotations_all_proteins_download', {
      entry_type: this.getEntryTypeForGa(),
      assembly_type: this.getAssemblyTypeForGa(),
    });
    this.isDownloadingAll.set(true);
    this.downloadAllFailed.set(false);
    try {
      const perAccession = await Promise.all(accessions.map((accession) => this.getAllAnnotationsForAccession(accession)));
      triggerCsvDownload(annotationsToCsv(perAccession.flat(), this.COPYRIGHT_RESTRICTED_MESSAGE), getAnnotationCsvFileName());
    } catch (e) {
      console.error('[TextAnnotations] Download annotations failed', e);
      this.downloadAllFailed.set(true);
    } finally {
      this.isDownloadingAll.set(false);
    }
  }

  private async getAllAnnotationsForAccession(accession: string): Promise<LlmAnnotation[]> {
    const loaded = this.llmAnnotationsData();
    if (loaded?.uniprotAccession === accession) return loaded.data ?? [];
    const cached = this.downloadedAccessionData.get(accession);
    if (cached) return cached;
    try {
      const url = this.configService.getConfig().llmAnnotationsApiUrl + accession;
      const data = await firstValueFrom(this.commonService.getApiData(url));
      const annotations = parseLlmAnnotationsResponse(data, accession).data;
      this.downloadedAccessionData.set(accession, annotations);
      return annotations;
    } catch (e) {
      // 404 = no annotations for this accession (the tab treats it as empty too); anything else
      // aborts the download rather than silently writing a CSV that's missing a chain.
      if (e instanceof HttpErrorResponse && e.status === 404) return [];
      throw e;
    }
  }

  public getArticleLink(annotation: LlmAnnotation): string | undefined {
    if (!annotation.pmcId) return undefined;
    return getSentenceArticleLink(annotation.sentence, annotation.exact, annotation.pmcId);
  }

  public getAuthorYear(annotation: LlmAnnotation): PublicationAuthorYear | undefined {
    return getPublicationAuthorYear(annotation.authors, annotation.publicationYear);
  }

  public roundScore(score: number): number {
    return Math.floor((score ?? 0) * 100) / 100;
  }

  // ---- Mol* + PAE ----------------------------------------------------------------------
  // Ported from AnnotationsTabComponent's own working implementation (same functions, same
  // approach), minus anything AlphaMissense-specific — this tab has no AM data. Always shows
  // the top-level entry's structure/PAE, for both monomer and complex entries — no per-chain
  // switching yet (neither PaeViewComponent nor this pattern support that today).

  // The ported smart-seq-viewer library dispatches a `protvista-click` DOM event on residue
  // click (gated by viewerOptions.externalEvents + the entityId/chainId inputs, both already
  // wired) — the same event AFDB's own codebase already uses elsewhere (e.g.
  // heatmap.component.ts's own pv-angular-wrapper, with [externalEvents]="true") to drive Mol*
  // from a 2D residue click. The library itself never exposes an Angular @Output for this, so a
  // document-level listener is the only hook point — but that also means this listener sees
  // EVERY `protvista-click` dispatch on the page, not just this tab's own sequence viewer.
  // AnnotationsTabComponent's heatmap (via ProtvistaOnTrackMouseEvents.triggerExternalClickEvents)
  // dispatches the exact same event/detail shape; since mat-tab-group's matTabContent keeps a
  // once-visited tab's component alive rather than destroying it, both tabs' listeners are live
  // simultaneously once each has been opened. Every other consumer of this event in the codebase
  // (heatmap.component.ts's own listener, and the library's internal handleExternalClickEvent)
  // filters on entityId/chainId before acting — this one didn't, so a residue click in the OTHER
  // tab's heatmap would silently filter this tab's table and move its own Mol* selection.
  @HostListener('document:protvista-click', ['$event'])
  public handleSeqViewerResidueClick(event: Event): void {
    const detail = (event as CustomEvent)?.detail;
    const feature = detail?.feature;
    if (feature?.entityId !== this.activeEntityId() || feature?.chainId !== this.activeChainId()) return;
    const localResidue = detail?.start !== undefined ? Number(detail.start) : NaN;
    if (Number.isNaN(localResidue)) return;
    // The viewer emits its own local/chain numbering (confirmed against
    // AnnotationsTabComponent, which passes this same detail.start straight to Mol* as
    // start_residue_number unmodified) — convert to absolute for the table filter, but Mol*
    // gets the already-local value as-is.
    this.residueFilter.set(this.toAbsoluteResidue(localResidue));
    this.selectAnnotationResidue(localResidue);
  }

  // Mol* residue click → table filter + sequence panel + Mol* highlight, same as a sequence-panel
  // click. Listened to on this tab's own Mol* container (not document), so other tabs' Mol*
  // viewers can't trigger it. Clicks on a chain other than the active one are ignored; empty-space
  // clicks are handled by onMolstarEmptyClick below.
  private molstarContainer?: HTMLElement;
  public onMolstarClick = (event: Event): void => {
    const data = (event as any)?.eventData;
    const localResidue = Number(data?.residueNumber);
    if (!data || !Number.isFinite(localResidue)) return;
    if (data.auth_asym_id && data.auth_asym_id !== this.activeChainId()) return;
    this.residueFilter.set(this.toAbsoluteResidue(localResidue));
    this.selectAnnotationResidue(localResidue);
    this.selectSeqViewerResidue(localResidue);
  };

  // Clicking empty space in Mol* = "Show all". pdbe-molstar's PDB.molstar.click DOM event only
  // fires for residues, so empty clicks come from Mol*'s own interaction API instead.
  private emptyClickSubscription?: { unsubscribe: () => void };
  private subscribeToMolstarEmptyClick(): void {
    const click$ = this.pdbeMolstar?.plugin?.behaviors?.interaction?.click;
    if (this.emptyClickSubscription || !click$) return;
    this.emptyClickSubscription = click$.subscribe((event: any) => this.onMolstarEmptyClick(event?.current?.loci?.kind));
  }

  public onMolstarEmptyClick(lociKind: string | undefined): void {
    if (lociKind !== 'empty-loci' || this.residueFilter() === undefined) return;
    this.clearResidueFilter();
  }

  // The library's own right-side residue-details panel has its own × close button
  // (seq-viewer-ui-sidebar.ts's handleSidebarClose), which calls the library's
  // unselectResidueState() and dispatches this `smartSeqViewerUnselect` DOM event — separate
  // from and not routed through handleSeqViewerResidueClick above. Without this listener,
  // closing the panel that way left the table stuck on its "residue not described" empty
  // state and Mol* still showing the previous selection, since only the table's own
  // "Show all" link called clearResidueFilter() directly.
  @HostListener('document:smartSeqViewerUnselect')
  public handleSeqViewerResidueDeselect(): void {
    this.clearResidueFilter();
  }

  async selectAnnotationResidue(residue: number): Promise<void> {
    if (!this.molLoadComplete || !this.pdbeMolstar) return;
    // Scope to the active chain so a residue number that repeats across a homodimer's
    // copies (or collides with a different chain in a heterodimer) selects only the
    // instance the annotation actually belongs to.
    const chainId = this.activeChainId();
    const chainScope = chainId ? { auth_asym_id: chainId } : {};
    // Redraw the base view first so a previously clicked residue's ball-and-stick is removed,
    // then add this one on top with keepRepresentations so the green dots stay.
    await this.refreshMolstarBaseView();
    await this.queueMolstar(async () => {
      await this.pdbeMolstar.visual.select({
        data: [{ residue_number: residue, ...chainScope, color: null, representation: 'ball-and-stick', focus: true }],
        keepColors: true,
        keepRepresentations: true,
      });
      await this.pdbeMolstar.visual.highlight({ data: [{ residue_number: residue, ...chainScope }] });
    });
  }

  clearSeqSelection(): void {
    const selectedSeqElements = this.el.nativeElement.querySelectorAll(
      '.ms-pae-selection, .ms-pae-select-green, .ms-pae-select-orange, .ms-pae-select-yellow'
    );
    if (selectedSeqElements) {
      selectedSeqElements.forEach((selEle) => {
        selEle.classList.remove('ms-pae-selection');
        selEle.classList.remove('ms-pae-select-green');
        selEle.classList.remove('ms-pae-select-orange');
        selEle.classList.remove('ms-pae-select-yellow');
      });
    }
  }

  applySeqSelection(selectionData): void {
    this.clearSeqSelection();
    selectionData.forEach((ele) => {
      for (let seqid = ele.start_residue_number; seqid <= ele.end_residue_number; seqid++) {
        const seqElement = this.el.nativeElement.querySelector(`.msp-sequence-present[data-seqid="${seqid}"`);
        if (seqElement) {
          seqElement.classList.add(`${ele.class}`);
        }
      }
    });
  }

  cmSelectionUpdate(cmSelectedRegions: PaeSelectedRegions): void {
    this.handleCmUpdate(cmSelectedRegions);
  }

  async handleCmUpdate(regions: PaeSelectedRegions): Promise<void> {
    if (!this.molLoadComplete) return;

    if (!regions) {
      this.clearSeqSelection();
      await this.pdbeMolstar.visual.clearSelection(undefined, { keepRepresentations: true });
      this.pdbeMolstar.visual.reset({ camera: true });
      // PAE emits an empty region on its own initial load too — restore dots + active chain dimming/focus.
      await this.refreshMolstarBaseView();
      return;
    }

    // Real per-chain offsets (from the PAE matrix's own multi-chain breakdown), same source
    // MacromoleculesTabComponent uses — without this, a homodimer/heterodimer's brush
    // selection maps to the wrong residue numbers/chain, since a single-chain fallback treats
    // the whole complex as one giant chain instead of splitting at each chain's real length.
    const proteinChains: Chain[] = this.paeDataService.chains();
    const selectionChains: Chain[] = proteinChains.length > 0
      ? proteinChains
      : [{ name: 'ProteinA', label_asym_id: undefined, length: this.entryData().sequenceEnd, sequenceEnd: this.entryData().sequenceEnd, sequenceStart: 1 }];
    const selectionData = this.utilService.getSelectionData(regions, selectionChains);

    this.pdbeMolstar.visual.select({
      data: selectionData,
      nonSelectedColor: '#707372',
      keepRepresentations: true,
    });

    this.applySeqSelection(selectionData);
  }

  initMolstar(): void {
    this.structure = {
      model_url: this.entryData().bcifUrl,
      model_format: 'cif',
      binary: true,
    };

    const modelCreatedDate = new Date(this.entryData().modelCreatedDate);
    this.lastUpdateDate = `${modelCreatedDate.getDate()} ${modelCreatedDate.toLocaleString(
      'default',
      { month: 'long' }
    )} ${modelCreatedDate.getFullYear()}`;
    this.molstarInitialTimeoutId = setTimeout(() => {
      this.handleMolstar(this.structure);
    }, 10);
  }

  async handleMolstar(structure: Structure): Promise<void> {
    const options = {
      customData: {
        url: structure.model_url,
        format: structure.model_format,
        binary: structure.binary,
      },
      subscribeEvents: false,
      bgColor: { r: 255, g: 255, b: 255 },
      alphafoldView: true,
      reactive: true,
      sequencePanel: true,
      hideCanvasControls: ['animation'],
      landscape: true,
      hideControls: true,
      validationAnnotation: true,
      granularity: 'residue',
    };

    if (this.isDestroyed) return;

    if (this.pdbeMolstar) {
      this.pdbeMolstar.visual.update(options, true);
    } else {
      const ele = this.el.nativeElement.querySelector('.text-annotations-molstar-container');

      if (ele) {
        this.molstarInitAttempts = 0;
        await this.molstarPluginService.loadPlugin();
        // Component may have been destroyed while loadPlugin() (a CDN fetch on first use) was
        // in flight — don't create/render into an element that's since been torn down.
        if (this.isDestroyed) return;
        const pluginInstance = this.molstarPluginService.createInstance();
        this.pdbeMolstar = pluginInstance;
        const layout = [
          { target: ele, component: this.molstarPluginService.getClass().UIComponents.FullLayoutNoControlsUnlessExpanded },
        ];
        this.pdbeMolstar.render(layout, options);
        this.molstarContainer = ele;
        ele.addEventListener('PDB.molstar.click', this.onMolstarClick);

        this.pdbeMolstar.events.loadComplete.subscribe(() => {
          this.molLoadComplete = true;
          // The redraw effect no-ops until Mol* is ready, so draw the default view now.
          this.refreshMolstarBaseView();
          this.subscribeToMolstarEmptyClick();
        });
      } else if (this.molstarInitAttempts < TextAnnotationsTabComponent.MAX_MOLSTAR_INIT_ATTEMPTS) {
        // .text-annotations-molstar-container is behind @if (entryData()) — for a cold store
        // load, entryData() can still be undefined at this point even though the signal was
        // already set synchronously, because Angular hasn't flushed that change into the
        // template yet. Retry instead of giving up forever.
        this.molstarInitAttempts++;
        this.molstarRetryTimeoutId = setTimeout(() => this.handleMolstar(structure), 500);
      }
    }
  }
}
